import API_URLS from '@Constants/apiUrls.constants';
import {
  AddForwardingNumberRequest,
  AreaCodeData,
  AvailableNumber,
  AvailableNumberResponse,
  BuyPhoneNumberRequest,
  BuyPhoneNumberResponse,
  CallerIdData,
  CallerIdList,
  CallerIdRotationData,
  CallerIdRotationList,
  ChangeRotationEnablingRequest,
  ChangeRotationEnablingResponse,
  DeleteCallerIdRequest,
  DeleteRotationCallerIdRequest,
  ForwardingNumberData,
  GetTprSettingsRequest,
  GetTprSettingsResponse,
  NumberSharingValidationRequest,
  NumberSharingValidationResponse,
  SendSMSForNumberSharingRequest,
  SendSMSForNumberSharingResponse,
  VerifyCallerRequest,
  VerifyCallerResponse,
  StirShakenRequest,
  ProviderCallerIdData,
  VerifyCallerSubmitVerificationCodeResponse,
  VerifyCallerSubmitVerificationCodeRequest,
  CheckCallerIdRequest,
  CheckCallerIdResponse,
  CheckCallerIdFileResponse,
  CheckCallerIdFileRequest,
} from '@Models/callerIds.models';

import { baseApi } from './baseApi.service';

export const userCallerIdsApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [
      'CommonCallerIds',
      'RotationCallerId',
      'ForwardingNumber',
      'TprSettings',
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      // Get a list of caller ids
      getVerifiedCallersList: builder.query<
        ProviderCallerIdData[],
        CallerIdList
      >({
        query: ({ userId }) =>
          `${API_URLS.USER_CALLER_IDS.GET_CALLER_ID_LIST}${
            userId ? `?userId=${userId}&` : '?'
          }`,
        providesTags: ['CommonCallerIds'],
      }),
      // Polling to keep a check on the status of the caller
      verifyCallerStatus: builder.query<CallerIdData, string>({
        query: (id) =>
          `${API_URLS.USER_CALLER_IDS.VERIFY_CALLER_STATUS_BY_POLLING}/${id}`,
      }),
      // verify caller by phone number twilio
      verifyCallerTwilio: builder.mutation<
        VerifyCallerResponse,
        VerifyCallerRequest
      >({
        query: (body) => ({
          url: API_URLS.USER_CALLER_IDS.VERIFY_CALLER_ID_TWILIO,
          method: 'POST',
          body,
        }),
      }),
      // verify caller by sms telnyx
      verifyCallerTelnyx: builder.mutation<
        VerifyCallerResponse,
        VerifyCallerRequest
      >({
        query: (body) => ({
          url: API_URLS.USER_CALLER_IDS.VERIFY_CALLER_ID_TELNYX,
          method: 'POST',
          body,
        }),
      }),
      // submit verification code for telnyx
      submitVerificationCodeTelnyx: builder.mutation<
        VerifyCallerSubmitVerificationCodeResponse,
        VerifyCallerSubmitVerificationCodeRequest
      >({
        query: (body) => ({
          url: API_URLS.USER_CALLER_IDS.VERIFY_CALLER_ID_TELNYX_SUBMIT_CODE,
          method: 'POST',
          body,
        }),
      }),
      // add number to stir/shaken
      stirShaken: builder.mutation<void, StirShakenRequest>({
        query: (body) => ({
          url: `${API_URLS.USER_CALLER_IDS.STIR_SHAKEN}`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['RotationCallerId'],
      }),
      // is user support stir/shaken
      supportStirShaken: builder.query<boolean, string>({
        query: (userId) =>
          `${API_URLS.USER_CALLER_IDS.SUPPORT_STIR_SHAKEN}${
            userId ? `?userId=${userId}&` : '?'
          }`,
      }),
      // delete caller by id
      deleteCallerById: builder.mutation<void, DeleteCallerIdRequest>({
        query: (body) => ({
          url: `${API_URLS.USER_CALLER_IDS.DELETE_CALLER_BY_ID}`,
          method: 'DELETE',
          body,
        }),
        invalidatesTags: ['CommonCallerIds', 'RotationCallerId'],
      }),
      // Get a list of all rotation caller ids
      getRotationCallerIdList: builder.query<
        CallerIdRotationData[],
        CallerIdRotationList
      >({
        query: ({ userId, provider }) =>
          `${API_URLS.USER_CALLER_IDS.GET_CALLER_ID_ROTATION_LIST}${
            userId ? `?userId=${userId}&` : '?'
          }status=all&provider=${provider}`,
        providesTags: ['RotationCallerId'],
      }),
      // Delete rotation callerId
      deleteRotationCallerId: builder.mutation<
        void,
        DeleteRotationCallerIdRequest
      >({
        query: (body) => ({
          url: `${API_URLS.USER_CALLER_IDS.DELETE_ROTATION_CALLER_ID}`,
          method: 'DELETE',
          body,
        }),
        invalidatesTags: ['RotationCallerId', 'CommonCallerIds', 'TprSettings'],
      }),
      // Verify own rotation caller id
      verifyRotationCaller: builder.mutation<
        VerifyCallerResponse,
        VerifyCallerRequest
      >({
        query: (body) => ({
          url: API_URLS.USER_CALLER_IDS.VERIFY_ROTATION_CALLER_ID,
          method: 'POST',
          body,
        }),
      }),
      // Polling to keep a check on the status of the rotation caller id
      verifyRotationCallerStatus: builder.query<CallerIdRotationData, string>({
        query: (id) =>
          `${API_URLS.USER_CALLER_IDS.VERIFY_ROTATION_CALLER_STATUS_BY_POLLING}/${id}`,
      }),
      // get a list of all available phone numbers
      getAvailablePhoneNumbers: builder.mutation<
        AvailableNumberResponse[],
        AvailableNumber
      >({
        query: (body) => ({
          url: `${API_URLS.USER_CALLER_IDS.GET_CALLER_ID_ROTATION_LIST}/available-provider-numbers`,
          method: 'POST',
          body,
        }),
      }),
      // buy system phone number
      buyPhoneNumber: builder.mutation<
        BuyPhoneNumberResponse,
        BuyPhoneNumberRequest
      >({
        query: (body) => ({
          url: `${API_URLS.USER_CALLER_IDS.GET_CALLER_ID_ROTATION_LIST}/buy-number`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['RotationCallerId', 'CommonCallerIds', 'TprSettings'],
      }),
      // Get a list of all area codes
      getAreaCodeList: builder.query<AreaCodeData[], void>({
        query: () => API_URLS.USER_CALLER_IDS.GET_AREA_CODES,
      }),
      // Change rotation callerId enabling
      changeEnablingRotationCallerById: builder.mutation<
        ChangeRotationEnablingResponse,
        ChangeRotationEnablingRequest
      >({
        query: (body) => ({
          url: `${API_URLS.USER_CALLER_IDS.CHANGE_ENABLING_ROTATION_CALLER}`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['RotationCallerId'],
      }),
      // Get forwarding number
      getForwardingNumber: builder.query<
        ForwardingNumberData | undefined,
        void
      >({
        query: () => API_URLS.USER_CALLER_IDS.FORWARDING_NUMBER,
        providesTags: ['ForwardingNumber'],
      }),
      // Add forwarding number
      addForwardingNumber: builder.mutation<void, AddForwardingNumberRequest>({
        query: (body) => ({
          url: `${API_URLS.USER_CALLER_IDS.FORWARDING_NUMBER}`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['ForwardingNumber'],
      }),
      // Delete forwarding number
      deleteForwardingNumberById: builder.mutation<void, string>({
        query: (id) => ({
          url: `${API_URLS.USER_CALLER_IDS.FORWARDING_NUMBER}/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['ForwardingNumber'],
      }),
      // Send sms for number sharing
      sendSmsForNumberSharing: builder.mutation<
        SendSMSForNumberSharingResponse,
        SendSMSForNumberSharingRequest
      >({
        query: (body) => ({
          url: `${API_URLS.USER_CALLER_IDS.SEND_SMS_FOR_NUMBER_SHARING}`,
          method: 'POST',
          body,
        }),
      }),
      // Send pin for number sharing validation
      sendPinForNumberSharing: builder.mutation<
        NumberSharingValidationResponse,
        NumberSharingValidationRequest
      >({
        query: (body) => ({
          url: `${API_URLS.USER_CALLER_IDS.NUMBER_SHARING_VALIDATION}`,
          method: 'POST',
          body,
        }),
      }),
      // Get tpr settings by user id
      getTprSetting: builder.query<
        GetTprSettingsResponse,
        GetTprSettingsRequest
      >({
        query: ({ id, provider }) => {
          const queryParams = [];
          if (id) {
            queryParams.push(`userId=${id}`);
          }
          if (provider) {
            queryParams.push(`provider=${provider}`);
          }

          const queryString =
            queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
          return `${API_URLS.USER_CALLER_IDS.GET_TPR_SETTINGS}${queryString}`;
        },
        providesTags: ['TprSettings'],
      }),
      // Check single caller Id
      checkCallerId: builder.mutation<
        CheckCallerIdResponse,
        CheckCallerIdRequest
      >({
        query: (body) => ({
          url: `${API_URLS.USER_CALLER_IDS.CHECK_CALLER_ID}`,
          method: 'POST',
          body,
        }),
      }),
      checkCallerIdFile: builder.mutation<
        CheckCallerIdFileResponse,
        CheckCallerIdFileRequest
      >({
        query: ({ file }) => {
          const formData = new FormData();
          formData.append('file', file);

          return {
            url: `${API_URLS.USER_CALLER_IDS.CHECK_CALLER_ID_FILE}`,
            method: 'POST',
            body: formData,
          };
        },
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetVerifiedCallersListQuery,
  useVerifyCallerStatusQuery,
  useVerifyCallerTwilioMutation,
  useVerifyCallerTelnyxMutation,
  useSubmitVerificationCodeTelnyxMutation,
  useDeleteCallerByIdMutation,
  useGetRotationCallerIdListQuery,
  useDeleteRotationCallerIdMutation,
  useStirShakenMutation,
  useSupportStirShakenQuery,
  useVerifyRotationCallerMutation,
  useVerifyRotationCallerStatusQuery,
  useGetAvailablePhoneNumbersMutation,
  useBuyPhoneNumberMutation,
  useGetAreaCodeListQuery,
  useChangeEnablingRotationCallerByIdMutation,
  useGetForwardingNumberQuery,
  useAddForwardingNumberMutation,
  useDeleteForwardingNumberByIdMutation,
  useSendSmsForNumberSharingMutation,
  useSendPinForNumberSharingMutation,
  useGetTprSettingQuery,
  useCheckCallerIdMutation,
  useCheckCallerIdFileMutation,
} = userCallerIdsApi;
